import Head from 'next/head'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { getShopsMetaLinks } from 'lib/utils/shop-lang'
import StateStorage from 'lib/state-storage'
import env from 'config/env'
import { DEF_HEAD_META_DATA } from './const'
import generateBreadcrumbs, { getDeeplink } from './utils'

const SMARTECH_SCRIPT_SRC = '//cdnt.netcoresmartech.com/smartechclient.js'

function loadSmartechScript(authUserId) {
  if (document.querySelector(`script[src="${SMARTECH_SCRIPT_SRC}"]`)) {
    return
  }

  const script = document.createElement('script')
  script.src = SMARTECH_SCRIPT_SRC
  script.async = true
  script.onload = () => {
    window.smartech('create', env.SMARTECH_CREATE_ID)
    window.smartech('register', env.SMARTECH_REGISTER_ID)
    window.smartech('identify', authUserId)
  }
  document.body.appendChild(script)
}

function trimProductPath(productPath) {
  const productId = productPath.replace(/-.+/gi, '')
  return productId
}

/**
 * For handling the sub category path that we have to support
 *
 * Check the file `/server/router.js` at `validCategory2Prefix`
 * @param {string[]} category eg. sub category and category = ['features', 'tops'] or category and category filter = ['tops', 'crop-tops']
 */
export function getCategoryPath(category) {
  if (
    !category?.length ||
    (category[0] !== 'features' && category[0] !== 'lookbooks')
  ) {
    return category
  }

  const parsedCategory = [...category]

  parsedCategory.shift()

  return parsedCategory
}

export function getCategoryCleanedPath(path) {
  if (!path) {
    return ''
  }

  const [, , ...category] = path.split('/')
  const parsedCategory = getCategoryPath(category)

  return `clothes/${parsedCategory.join('/')}`
}

export function getCategoryOriginPath(path) {
  if (!path) {
    return ''
  }

  const [, shop, lang, , ...category] = path.split('/')
  const parsedCategory = getCategoryPath(category)

  return `/${shop}/${lang}/clothes/${parsedCategory.join('/')}`
}

function getPathAfterCheckWithPageName(path, pageName, isCanonical) {
  const otherPath = isCanonical
    ? `${path}`
    : `${trimProductPath(path.slice(1))}`
  const productPath = `${otherPath}.html`
  const categoryPath = isCanonical
    ? getCategoryOriginPath(path)
    : getCategoryCleanedPath(path)
  switch (pageName) {
    case 'category':
      return categoryPath
    case 'product':
      return productPath
    default:
      return otherPath
  }
}

// Best-effort hint so the browser starts fetching the LCP (hero product)
// image from the initial HTML, instead of only discovering it once
// ResponsiveImage measures the DOM client-side and sets a real `src` — see
// components/images/responsive-image/index.js. Sized for a common desktop
// viewport; if the client ends up requesting a different size, this preload
// is simply unused (no duplicate render, no wasted re-fetch beyond the
// preloaded bytes), so a mismatch only costs bandwidth, not correctness.
const LCP_IMAGE_PRELOAD_WIDTH = 575
const LCP_IMAGE_PRELOAD_HEIGHT = 765

function getLcpImagePreloadUrl(image) {
  if (!image) {
    return null
  }
  return `${image}?auto=compress,format&fm=webp,jpg,png&w=${LCP_IMAGE_PRELOAD_WIDTH}&h=${LCP_IMAGE_PRELOAD_HEIGHT}`
}

const CustomHead = ({
  cleanedPath,
  deeplink: defaultDeeplink,
  description,
  image,
  originPath,
  pageId,
  pageName,
  pathPrefix,
  preloadImage,
  title,
  schema,
}) => {
  const deeplink = getDeeplink(pageName, pageId) || defaultDeeplink
  const links = getShopsMetaLinks(
    pathPrefix,
    getPathAfterCheckWithPageName(cleanedPath, pageName, false),
  )
  const schemaList = schema || generateBreadcrumbs()
  const authUserId = String(StateStorage.getAuthUser()?.id_customer ?? '')
  // `pageName` is only known client-side post-hydration (see CommonLayout's
  // getPageName(), gated on `typeof document`), so it can't be used to decide
  // this server-side. `preloadImage` is passed explicitly by the page itself
  // instead, so it's available identically during SSR and on the client.
  const lcpImagePreloadUrl = preloadImage ? getLcpImagePreloadUrl(image) : null

  useEffect(() => {
    loadSmartechScript(authUserId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadSmartechScript(authUserId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Head>
      <title>{title || DEF_HEAD_META_DATA.title}</title>
      {lcpImagePreloadUrl && (
        <link
          rel="preload"
          as="image"
          href={lcpImagePreloadUrl}
          fetchpriority="high"
        />
      )}
      <meta
        name="description"
        content={description || DEF_HEAD_META_DATA.description}
      />
      <meta
        property="al:ios:url"
        content={deeplink || DEF_HEAD_META_DATA.deeplink}
      />
      <meta
        property="al:android:url"
        content={deeplink || DEF_HEAD_META_DATA.deeplink}
      />
      <meta property="og:image" content={image || DEF_HEAD_META_DATA.image} />
      <meta property="og:title" content={title || DEF_HEAD_META_DATA.title} />
      <meta
        property="og:description"
        content={description || DEF_HEAD_META_DATA.description}
      />
      <link
        rel="canonical"
        href={`${pathPrefix}${getPathAfterCheckWithPageName(
          originPath,
          pageName,
          true,
        )}`}
      />
      {links.map(({ href, hrefLang }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}
      {schemaList && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: schemaList }}
        />
      )}
    </Head>
  )
}

CustomHead.defaultProps = {
  cleanedPath: '',
  deeplink: '',
  description: '',
  image: '',
  originPath: '',
  pageId: '',
  pageName: '',
  pathPrefix: '',
  preloadImage: false,
  title: '',
  schema: '',
}

CustomHead.propTypes = {
  description: PropTypes.string,
  deeplink: PropTypes.string,
  image: PropTypes.string,
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cleanedPath: PropTypes.string,
  originPath: PropTypes.string,
  pathPrefix: PropTypes.string,
  pageName: PropTypes.string,
  preloadImage: PropTypes.bool,
  title: PropTypes.string,
  schema: PropTypes.string,
}

export default CustomHead
